import _styled, { css as _css, default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7, default as _styled8, default as _styled9, default as _styled10, default as _styled11, default as _styled12, default as _styled13, default as _styled14, default as _styled15, default as _styled16, default as _styled17, default as _styled18, default as _styled19, default as _styled20, default as _styled21, default as _styled22, default as _styled23, default as _styled24, default as _styled25 } from "styled-components";
import React from "react";
import { useRouter } from "next/router";
import { graphql, useFragment, type FragmentType } from "~/gql/runtime";
import { Icon } from "~/features/ui/icon";
import { useNavLayout } from "~/features/app/app-header-v2/context";
import { Link } from "~/features/ui/link";
import { useDropdownAnimation } from "~/features/app/app-header-v2/nav-links/use-dropdown-animation";
import { CategoryLink } from "~/features/app/app-header-v2/nav-links/category-link";
import { ProductLink } from "~/features/app/app-header-v2/nav-links/product-link";
import type { NavDropdownCategory, NavDropdownProduct, NavDropdownQuickLink, CategoryLinkType } from "~/features/app/app-header-v2/nav-links/nav-dropdown-types";
import { isLinkValid } from "~/features/ui/link/utils";
import { takeFirst } from "~/features/app/utils";
import Plus from "~/public/icons/plus.svg";
const fragment = graphql(`
  fragment NavDropdownV2 on navLinks_dropdown_BlockType {
    text: baseText
    links: children {
      __typename
      ...NavLink
      ... on navLinks_dropdown_BlockType {
        id
        text: baseText
        links: children {
          __typename
          ...NavLink
          ... on navLinks_link_BlockType {
            link: baseLink {
              ... on baseLink_Entry_LinkType {
                element {
                  __typename
                  ... on hardware_default_Entry {
                    id
                    title
                    url
                    productCaption
                    productGeneration
                    productImage {
                      ...Asset
                    }
                  }
                  ... on software_default_Entry {
                    id
                    title
                    url
                    productCaption
                    productImage {
                      ...Asset
                    }
                  }
                }
              }
            }
          }
          ... on navLinks_quickLinks_BlockType {
            id
            text: baseText
            links: children {
              __typename
              ...NavLink
            }
          }
        }
      }
      ... on navLinks_quickLinks_BlockType {
        id
        text: baseText
        links: children {
          __typename
          ...NavLink
        }
      }
    }
  }
`);
const hoverStyle = {
  transparent: {
    "@media (hover: hover)": {
      "&:hover": {
        "backgroundColor": "rgba(0, 7, 4, 0.3)"
      }
    }
  },
  solid: {
    "@media (hover: hover)": {
      "&:hover": {
        "--tw-bg-opacity": "1",
        "backgroundColor": "rgb(255 255 255 / var(--tw-bg-opacity))"
      }
    }
  }
};
const dropdownContainerStyles = /*#__PURE__*/_css(["transition:all 0.4s ease-out;", " ", " left:0;right:0;z-index:102;"], {
  "position": "absolute",
  "top": "100%",
  "willChange": "transform"
}, {
  "overflow": "hidden"
});
const dropdownContentStyles = /*#__PURE__*/_css(["", " &::-webkit-scrollbar{width:4px;}&::-webkit-scrollbar-track{background:rgba(255,255,255,0.1);}&::-webkit-scrollbar-thumb{background:rgba(255,255,255,0.3);border-radius:4px;}&::-webkit-scrollbar-thumb:hover{background:rgba(255,255,255,0.5);}"], {
  "overflowY": "auto"
});
const gridStyles = /*#__PURE__*/_css(["", " grid-template-columns:44.8rem 38.2rem 1fr;grid-auto-rows:1fr;"], {
  "display": "grid"
});
const gridStylesWithoutProducts = /*#__PURE__*/_css(["", " grid-template-columns:44.8rem 1fr;grid-auto-rows:1fr;"], {
  "display": "grid"
});

// Helper function to process CMS data into dropdown structure
function processDropdownData(links: Array<{
  __typename?: string;
  id?: string;
  text?: string;
  link?: unknown;
  links?: unknown[];
}>): {
  categories: NavDropdownCategory[];
  quickLinks: NavDropdownQuickLink[];
  quickLinksLabel?: string;
} {
  const categories: NavDropdownCategory[] = [];
  const quickLinks: NavDropdownQuickLink[] = [];
  let quickLinksLabel: string | undefined;
  links?.forEach(item => {
    if (!item) return;

    // Handle simple link types as categories without products
    if (item.__typename === "navLinks_link_BlockType") {
      const linkData = takeFirst(item.link) as {
        newWindow?: boolean;
        text?: string;
        href?: string;
      } | undefined;
      if (isLinkValid(linkData)) {
        const type: CategoryLinkType = linkData?.newWindow ? "external" : "internal";
        const category = {
          id: item.id || `category-${categories.length}`,
          title: linkData?.text || "",
          description: "",
          type,
          url: linkData?.href || "#",
          products: []
        };
        categories.push(category);
      }
    }

    // Handle nested dropdowns with products (multi-level)
    if (item.__typename === "navLinks_dropdown_BlockType") {
      const products: NavDropdownProduct[] = [];
      const itemLinks = item.links as Array<{
        __typename?: string;
        id?: string;
        text?: string;
        link?: unknown;
        links?: unknown[];
      }> | undefined;
      itemLinks?.forEach(child => {
        if (child.__typename === "navLinks_link_BlockType" || child.link) {
          const linkData = takeFirst(child.link) as {
            text?: string;
            href?: string;
            element?: {
              __typename?: string;
              id?: string;
              title?: string;
              url?: string;
              productCaption?: string;
              productGeneration?: string;
              productImage?: unknown;
            };
          } | undefined;
          if (isLinkValid(linkData)) {
            const linkedEntry = linkData?.element;
            const isProduct = linkedEntry?.__typename === "hardware_default_Entry" || linkedEntry?.__typename === "software_default_Entry";
            let product;
            if (isProduct && linkedEntry) {
              product = {
                id: linkedEntry.id || child.id || `product-${products.length}`,
                title: linkData?.text || linkedEntry.title || "",
                description: linkedEntry.productCaption || "",
                url: linkedEntry.url || linkData?.href || "#",
                image: linkedEntry.productImage,
                generation: linkedEntry.productGeneration || ""
              };
            } else {
              product = {
                id: child.id || `product-${products.length}`,
                title: linkData?.text || "",
                description: "",
                url: linkData?.href || "#"
              };
            }
            products.push(product);
          }
        }

        // Quick links nested inside a sub-dropdown
        if (child.__typename === "navLinks_quickLinks_BlockType") {
          if (!quickLinksLabel) {
            quickLinksLabel = child.text || "Quick Links";
          }
          const childLinks = child.links as Array<{
            __typename?: string;
            id?: string;
            link?: unknown;
          }> | undefined;
          childLinks?.forEach(qlChild => {
            if (qlChild.__typename === "navLinks_link_BlockType" || qlChild.link) {
              const linkData = takeFirst(qlChild.link) as {
                text?: string;
                href?: string;
              } | undefined;
              if (isLinkValid(linkData)) {
                quickLinks.push({
                  id: qlChild.id || `quicklink-${quickLinks.length}`,
                  title: linkData?.text || "",
                  url: linkData?.href || "#"
                });
              }
            }
          });
        }
      });
      const category = {
        id: item.id || `category-${categories.length}`,
        title: item.text || "",
        description: "",
        type: "multi-level" as CategoryLinkType,
        products
      };
      categories.push(category);
    }

    // Handle quickLinks block
    if (item.__typename === "navLinks_quickLinks_BlockType") {
      // Store the label for the quickLinks section
      quickLinksLabel = item.text || "Quick Links";
      const itemLinks = item.links as Array<{
        __typename?: string;
        id?: string;
        link?: unknown;
      }> | undefined;
      itemLinks?.forEach(child => {
        if (child.__typename === "navLinks_link_BlockType" || child.link) {
          const linkData = takeFirst(child.link) as {
            text?: string;
            href?: string;
          } | undefined;
          if (isLinkValid(linkData)) {
            quickLinks.push({
              id: child.id || `quicklink-${quickLinks.length}`,
              title: linkData?.text || "",
              url: linkData?.href || "#"
            });
          }
        }
      });
    }
  });
  return {
    categories,
    quickLinks,
    quickLinksLabel
  };
}
export function NavDropdown(props: FragmentType<typeof fragment>) {
  const {
    text,
    links
  } = useFragment(fragment, props);
  const {
    navTheme,
    isPageHighlighted,
    activeDropdown,
    setActiveDropdown,
    setDropdownHeight,
    isAnnouncementBarVisible
  } = useNavLayout();
  const chromeTheme = isPageHighlighted ? "solid" : navTheme;
  const megaSurface = isPageHighlighted ? "light" : "dark";
  const dividerTw = megaSurface === "light" ? {
    "backgroundColor": "rgb(0 7 4 / 0.15)"
  } : {
    "backgroundColor": "rgba(255, 255, 255, 0.1)"
  };
  const router = useRouter();
  const dropdownRef = React.useRef<HTMLButtonElement>(null);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const categoriesRef = React.useRef<HTMLDivElement>(null);
  const productsRef = React.useRef<HTMLDivElement>(null);
  const quickLinksRef = React.useRef<HTMLDivElement>(null);
  const [selectedCategory, setSelectedCategory] = React.useState<string | null>(null);
  const [maxDropdownHeight, setMaxDropdownHeight] = React.useState<number>(0);
  const isOpen = activeDropdown === text;

  // Helper to check if a URL is active
  const isLinkActive = React.useCallback((url: string) => {
    if (!url) return false;

    // Get current path
    const currentPath = (router.asPath.split("?")[0] || "").split("#")[0] || "";

    // Extract pathname from URL (handles both full URLs and paths)
    let linkPath = url;
    try {
      // If it's a full URL (starts with http), extract just the pathname
      if (url.startsWith("http")) {
        const urlObj = new URL(url);
        linkPath = urlObj.pathname;
      }
    } catch (e) {
      // If URL parsing fails, use as-is
      linkPath = url;
    }

    // Remove query params and hash from linkPath
    linkPath = (linkPath.split("?")[0] || "").split("#")[0] || "";

    // Compare paths (remove trailing slashes)
    const cleanCurrent = currentPath.replace(/\/$/, "");
    const cleanLink = linkPath.replace(/\/$/, "");
    return cleanCurrent === cleanLink;
  }, [router.asPath]);

  // Check if any product in a category is active
  const isCategoryActive = React.useCallback((category: NavDropdownCategory) => {
    if (category.url && isLinkActive(category.url)) return true;
    if (category.products && category.products.length > 0) {
      return category.products.some(product => isLinkActive(product.url));
    }
    return false;
  }, [isLinkActive]);

  // Process CMS data into dropdown structure
  const dropdownData = React.useMemo(() => {
    if (!links || links.length === 0) return null;
    const processed = processDropdownData(links.filter((l): l is NonNullable<typeof l> => l !== null) as Array<{
      __typename?: string;
      id?: string;
      text?: string;
      link?: unknown;
      links?: unknown[];
    }>);
    return processed;
  }, [links]);

  // GSAP Animation hook
  const {
    animateInitial,
    animateProducts
  } = useDropdownAnimation();

  // Track if this is the initial open
  const isInitialOpenRef = React.useRef(true);

  // Check if any category has products (needs to be before useEffect hooks)
  const hasProducts = dropdownData ? dropdownData.categories.some(cat => cat.products && cat.products.length > 0) : false;

  // Calculate max dropdown height based on viewport
  React.useEffect(() => {
    const calculateMaxHeight = () => {
      const announcementBarHeight = isAnnouncementBarVisible ? parseInt(getComputedStyle(document.documentElement).getPropertyValue("--announcement-bar-height")) || 0 : 0;
      const headerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue("--header-height")) || 0;

      // Max height = viewport height - announcement bar - header - 60px breathing room
      const maxHeight = window.innerHeight - announcementBarHeight - headerHeight - 60;
      setMaxDropdownHeight(maxHeight);
    };
    calculateMaxHeight();
    window.addEventListener("resize", calculateMaxHeight);
    return () => window.removeEventListener("resize", calculateMaxHeight);
  }, [isAnnouncementBarVisible]);

  // Reset and set first multi-level category as default when this dropdown becomes active
  React.useEffect(() => {
    if (isOpen && dropdownData?.categories) {
      // Check if any category is already active based on the URL
      const hasActiveCategory = dropdownData.categories.some(cat => isCategoryActive(cat));

      // Only set the first multi-level category if no category is already active
      if (!hasActiveCategory) {
        // Find the first multi-level category (dropdown child with products)
        const firstMultiLevelCategory = dropdownData.categories.find(cat => cat.type === "multi-level" && cat.products && cat.products.length > 0);
        if (firstMultiLevelCategory) {
          setSelectedCategory(firstMultiLevelCategory.id);
        }
      } else {
        // If a category is active, select it
        const activeCategory = dropdownData.categories.find(cat => isCategoryActive(cat));
        if (activeCategory) {
          setSelectedCategory(activeCategory.id);
        }
      }
      isInitialOpenRef.current = true; // Mark as initial open
    } else if (!isOpen) {
      setSelectedCategory(null);
      isInitialOpenRef.current = true; // Reset for next open
    }
  }, [isOpen, dropdownData, text, isCategoryActive]);

  // Update dropdown height when content changes - use useLayoutEffect for immediate measurement
  React.useLayoutEffect(() => {
    if (isOpen && contentRef.current) {
      // Delay to ensure content is fully rendered, grid is calculated, and images are loading
      const timeoutId = setTimeout(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (contentRef.current) {
              // Measure individual column heights to get the tallest
              const categoriesHeight = categoriesRef.current?.offsetHeight || 0;
              const productsHeight = hasProducts && productsRef.current ? productsRef.current.offsetHeight : 0;
              const quickLinksHeight = quickLinksRef.current?.offsetHeight || 0;

              // Use the tallest column height
              const maxHeight = Math.max(categoriesHeight, productsHeight, quickLinksHeight);
              setDropdownHeight(maxHeight);
            }
          });
        });
      }, 100); // 100ms delay to let content stabilize

      return () => clearTimeout(timeoutId);
    } else {
      setDropdownHeight(0);
    }
  }, [isOpen, selectedCategory, setDropdownHeight, dropdownData, hasProducts]);

  // Recalculate height when images finish loading
  React.useEffect(() => {
    if (isOpen && contentRef.current) {
      const images = contentRef.current.querySelectorAll("img");
      let loadedCount = 0;
      const totalImages = images.length;
      if (totalImages === 0) return; // No images to wait for

      const recalculateHeight = () => {
        loadedCount++;
        // Only recalculate after all images are loaded
        if (loadedCount === totalImages) {
          setTimeout(() => {
            if (contentRef.current) {
              const categoriesHeight = categoriesRef.current?.offsetHeight || 0;
              const productsHeight = hasProducts && productsRef.current ? productsRef.current.offsetHeight : 0;
              const quickLinksHeight = quickLinksRef.current?.offsetHeight || 0;
              const maxHeight = Math.max(categoriesHeight, productsHeight, quickLinksHeight);
              setDropdownHeight(maxHeight);
            }
          }, 50);
        }
      };
      images.forEach(img => {
        if (img.complete) {
          recalculateHeight();
        } else {
          img.addEventListener("load", recalculateHeight);
          img.addEventListener("error", recalculateHeight);
        }
      });
      return () => {
        images.forEach(img => {
          img.removeEventListener("load", recalculateHeight);
          img.removeEventListener("error", recalculateHeight);
        });
      };
    }
  }, [isOpen, hasProducts, setDropdownHeight, selectedCategory]);

  // Initial animation when dropdown opens - animate all columns
  React.useEffect(() => {
    if (isOpen && isInitialOpenRef.current && categoriesRef.current) {
      // Check if products column exists before animating
      const shouldAnimateProducts = hasProducts && productsRef.current;

      // Small delay to ensure DOM is ready
      requestAnimationFrame(() => {
        animateInitial({
          categoriesRef,
          productsRef: shouldAnimateProducts ? productsRef : {
            current: null
          },
          quickLinksRef
        });
        isInitialOpenRef.current = false; // Mark as no longer initial
      });
    }
  }, [isOpen, animateInitial, hasProducts]);

  // Animate only products column when category changes (not on initial open)
  React.useEffect(() => {
    if (isOpen && !isInitialOpenRef.current && selectedCategory && hasProducts && productsRef.current) {
      // Small delay to ensure DOM is updated with new products
      requestAnimationFrame(() => {
        animateProducts(productsRef);
      });
    }
  }, [selectedCategory, isOpen, animateProducts, hasProducts]);
  function handleToggle() {
    if (isOpen) {
      setActiveDropdown(null);
      setSelectedCategory(null);
    } else {
      setActiveDropdown(text || null);
    }
  }
  if (!dropdownData) {
    return null;
  }
  const selectedCategoryData = dropdownData.categories.find(cat => cat.id === selectedCategory);
  return <>
      <_StyledButton ref={dropdownRef} aria-label={text || "Dropdown menu"} aria-expanded={isOpen} onClick={handleToggle} $_css2={[{
      "position": "relative",
      "display": "flex",
      "height": "100%",
      "alignItems": "center",
      "paddingLeft": "4rem",
      "paddingRight": "4rem",
      "transitionProperty": "color, background-color, border-color, text-decoration-color, fill, stroke",
      "transitionDuration": "250ms",
      "transitionTimingFunction": "ease-in-out"
    }, {
      "&::before": {
        "content": "var(--tw-content)",
        "position": "absolute",
        "left": "0",
        "right": "0",
        "top": "0",
        "height": "0.4rem",
        "--tw-bg-opacity": "1",
        "backgroundColor": "rgb(75 46 248 / var(--tw-bg-opacity))",
        "opacity": "0",
        "transitionProperty": "opacity",
        "transitionDuration": "250ms",
        "transitionTimingFunction": "ease-in-out",
        "--tw-content": "\"\""
      }
    }, isOpen && {
      "&::before": {
        "content": "var(--tw-content)",
        "opacity": "1"
      }
    }, hoverStyle[chromeTheme]]}>
        <_StyledDiv>
          <_StyledIcon icon={Plus} size="12" $_css3={[{
          "position": "absolute",
          "left": "0",
          "top": "0",
          "--tw-translate-x": "-100%",
          "transform": "translate(var(--tw-translate-x), var(--tw-translate-y)) rotate(var(--tw-rotate)) skewX(var(--tw-skew-x)) skewY(var(--tw-skew-y)) scaleX(var(--tw-scale-x)) scaleY(var(--tw-scale-y))",
          "--tw-translate-y": "-50%",
          "transitionProperty": "transform",
          "transitionDuration": "250ms",
          "transitionTimingFunction": "ease-in-out"
        }, isOpen ? {
          "--tw-rotate": "45deg",
          "transform": "translate(var(--tw-translate-x), var(--tw-translate-y)) rotate(var(--tw-rotate)) skewX(var(--tw-skew-x)) skewY(var(--tw-skew-y)) scaleX(var(--tw-scale-x)) scaleY(var(--tw-scale-y))"
        } : undefined]} />
          <span>{text}</span>
        </_StyledDiv>
      </_StyledButton>

      <_StyledDiv2 style={{
      maxHeight: isOpen ? `${Math.min(contentRef.current?.offsetHeight || 500, maxDropdownHeight)}px` : "0px",
      opacity: isOpen ? 1 : 0,
      pointerEvents: isOpen ? "auto" : "none"
    }}>
        <_StyledDiv3 ref={contentRef} style={{
        maxHeight: `${maxDropdownHeight}px`
      }} $_css4={[hasProducts ? gridStyles : gridStylesWithoutProducts, dropdownContentStyles, megaSurface === "light" ? {
        "--tw-text-opacity": "1",
        "color": "rgb(0 7 4 / var(--tw-text-opacity))"
      } : {
        "--tw-text-opacity": "1",
        "color": "rgb(255 255 255 / var(--tw-text-opacity))"
      }]}>
          {/* Column 1: Categories */}
          <_StyledDiv4 ref={categoriesRef}>
            {/* Vertical divider - right border */}
            <_StyledDiv5 data-category-divider $_css5={[{
            "position": "absolute",
            "bottom": "0",
            "right": "0",
            "top": "0",
            "width": "0.1rem"
          }, dividerTw]} />

            {dropdownData.categories.map((category, index) => {
            const isActive = isCategoryActive(category);
            const isLastCategory = index === dropdownData.categories.length - 1;
            return <_StyledDiv6 key={category.id} $_css6={[{
              "position": "relative"
            }, _css(["&:hover .blue-line{transform:scaleX(1);}"]), isActive && _css([".blue-line{transform:scaleX(1) !important;}"])]}>
                  <_StyledDiv7>
                    <CategoryLink category={category} isSelected={selectedCategory === category.id} isActive={isActive} onSelect={setSelectedCategory} onNavigate={() => setActiveDropdown(null)} surface={megaSurface} />
                  </_StyledDiv7>
                  {/* Lines container */}
                  <_StyledDiv8>
                    {/* Blue line that grows on hover/active */}
                    <_StyledDiv9 className="blue-line" $_css7={[{
                  "position": "absolute",
                  "inset": "0",
                  "--tw-bg-opacity": "1",
                  "backgroundColor": "rgb(75 46 248 / var(--tw-bg-opacity))",
                  "willChange": "transform"
                }, _css(["transform-origin:left center;transform:scaleX(0);transition:transform 0.6s cubic-bezier(0.125,0.425,0.27,1);"]), (selectedCategory === category.id || isActive) && _css(["transform:scaleX(1);"])]} />
                    {/* Base gray line */}
                    {!isLastCategory && <_StyledDiv10 data-category-line $_css8={[{
                  "height": "0.1rem"
                }, dividerTw]} />}
                  </_StyledDiv8>
                </_StyledDiv6>;
          })}
          </_StyledDiv4>

          {/* Column 2: Products - Only render if there are products */}
          {hasProducts && <_StyledDiv11 ref={productsRef} key={selectedCategory}>
              {/* Vertical divider - right border */}
              <_StyledDiv12 data-product-divider $_css9={[{
            "position": "absolute",
            "bottom": "0",
            "right": "0",
            "top": "0",
            "width": "0.1rem"
          }, dividerTw]} />

              {selectedCategoryData?.products.map((product, index) => {
            const isActive = isLinkActive(product.url);
            const isLastProduct = selectedCategoryData && index === selectedCategoryData.products.length - 1;
            return <_StyledDiv13 key={product.id} $_css10={[{
              "position": "relative"
            }, _css(["&:hover .blue-line{transform:scaleX(1);}&:hover .blue-square{transform:scale(1);}"]), isActive && _css([".blue-line{transform:scaleX(1) !important;}.blue-square{transform:scale(1) !important;}"])]}>
                    <_StyledDiv14>
                      <ProductLink product={product} isActive={isActive} onNavigate={() => setActiveDropdown(null)} surface={megaSurface} />
                    </_StyledDiv14>
                    {/* Lines container */}
                    <_StyledDiv15>
                      {/* Blue line that grows on hover/active */}
                      <_StyledDiv16 className="blue-line" $_css11={[{
                  "position": "absolute",
                  "inset": "0",
                  "--tw-bg-opacity": "1",
                  "backgroundColor": "rgb(75 46 248 / var(--tw-bg-opacity))",
                  "willChange": "transform"
                }, _css(["transform-origin:left center;transform:scaleX(0);transition:transform 0.6s cubic-bezier(0.125,0.425,0.27,1);"]), isActive && _css(["transform:scaleX(1);"])]} />
                      {/* Base gray line */}
                      {!isLastProduct && <_StyledDiv17 data-product-line $_css12={[{
                  "height": "0.1rem"
                }, dividerTw]} />}
                    </_StyledDiv15>
                  </_StyledDiv13>;
          })}
            </_StyledDiv11>}

          {/* Column 3: Quick Links */}
          {dropdownData.quickLinks.length > 0 && <_StyledDiv18 ref={quickLinksRef}>
              <_StyledH data-quicklink-title $_css13={[{
            "marginBottom": "1.6rem",
            "fontSize": "1.4rem",
            "lineHeight": "1.5",
            "letterSpacing": "0.014000000000000002rem",
            "fontWeight": "400"
          }, megaSurface === "light" ? {
            "color": "rgb(0 7 4 / 0.5)"
          } : {
            "color": "rgb(255 255 255 / 0.5)"
          }]}>
                {dropdownData.quickLinksLabel}
              </_StyledH>
              {dropdownData.quickLinks.map(link => {
            const isActive = isLinkActive(link.url);
            return <_StyledLink key={link.id} data-quicklink-item href={link.url} onClick={() => setActiveDropdown(null)} $_css14={[{
              "position": "relative",
              "display": "inline-block"
            }, {
              "fontSize": "1.4rem",
              "lineHeight": "1.5",
              "letterSpacing": "0.014000000000000002rem",
              "fontWeight": "400"
            }, megaSurface === "light" ? {
              "--tw-text-opacity": "1",
              "color": "rgb(0 7 4 / var(--tw-text-opacity))"
            } : {
              "--tw-text-opacity": "1",
              "color": "rgb(255 255 255 / var(--tw-text-opacity))"
            }, _css(["&:hover .quicklink-line{transform:scaleX(1);}"]), isActive && _css([".quicklink-line{transform:scaleX(1);}"])]}>
                    <_StyledSpan>{link.title}</_StyledSpan>
                    {/* Horizontal line that grows on hover/active */}
                    <_StyledDiv19>
                      <_StyledDiv20 className="quicklink-line" $_css15={[{
                  "position": "absolute",
                  "inset": "0",
                  "willChange": "transform"
                }, megaSurface === "light" ? {
                  "--tw-bg-opacity": "1",
                  "backgroundColor": "rgb(0 7 4 / var(--tw-bg-opacity))"
                } : {
                  "--tw-bg-opacity": "1",
                  "backgroundColor": "rgb(255 255 255 / var(--tw-bg-opacity))"
                }, _css(["transform-origin:left center;transform:scaleX(0);transition:transform 0.3s cubic-bezier(0.125,0.425,0.27,1);"]), isActive && _css(["transform:scaleX(1);"])]} />
                    </_StyledDiv19>
                  </_StyledLink>;
          })}
            </_StyledDiv18>}
        </_StyledDiv3>
      </_StyledDiv2>
    </>;
}
var _StyledButton = /*#__PURE__*/_styled("button").withConfig({
  componentId: "sc-sf-ij24gx-0"
})`${p => p.$_css2}`;
var _StyledDiv = _styled2("div").withConfig({
  componentId: "sc-sf-ij24gx-1"
})({
  "position": "relative"
});
var _StyledIcon = _styled3(Icon).withConfig({
  componentId: "sc-sf-ij24gx-2"
})`${p => p.$_css3}`;
var _StyledDiv2 = _styled4("div").withConfig({
  componentId: "sc-sf-ij24gx-3"
})`${dropdownContainerStyles}`;
var _StyledDiv3 = _styled5("div").withConfig({
  componentId: "sc-sf-ij24gx-4"
})`${p => p.$_css4}`;
var _StyledDiv4 = _styled6("div").withConfig({
  componentId: "sc-sf-ij24gx-5"
})({
  "position": "relative",
  "display": "flex",
  "flexDirection": "column"
});
var _StyledDiv5 = _styled7("div").withConfig({
  componentId: "sc-sf-ij24gx-6"
})`${p => p.$_css5}`;
var _StyledDiv6 = _styled8("div").withConfig({
  componentId: "sc-sf-ij24gx-7"
})`${p => p.$_css6}`;
var _StyledDiv7 = _styled9("div").withConfig({
  componentId: "sc-sf-ij24gx-8"
})({
  "overflow": "hidden"
});
var _StyledDiv8 = _styled10("div").withConfig({
  componentId: "sc-sf-ij24gx-9"
})({
  "position": "relative",
  "height": "0.1rem"
});
var _StyledDiv9 = _styled11("div").withConfig({
  componentId: "sc-sf-ij24gx-10"
})`${p => p.$_css7}`;
var _StyledDiv10 = _styled12("div").withConfig({
  componentId: "sc-sf-ij24gx-11"
})`${p => p.$_css8}`;
var _StyledDiv11 = _styled13("div").withConfig({
  componentId: "sc-sf-ij24gx-12"
})({
  "position": "relative",
  "display": "flex",
  "flexDirection": "column"
});
var _StyledDiv12 = _styled14("div").withConfig({
  componentId: "sc-sf-ij24gx-13"
})`${p => p.$_css9}`;
var _StyledDiv13 = _styled15("div").withConfig({
  componentId: "sc-sf-ij24gx-14"
})`${p => p.$_css10}`;
var _StyledDiv14 = _styled16("div").withConfig({
  componentId: "sc-sf-ij24gx-15"
})({
  "overflow": "hidden"
});
var _StyledDiv15 = _styled17("div").withConfig({
  componentId: "sc-sf-ij24gx-16"
})({
  "position": "relative",
  "height": "0.1rem"
});
var _StyledDiv16 = _styled18("div").withConfig({
  componentId: "sc-sf-ij24gx-17"
})`${p => p.$_css11}`;
var _StyledDiv17 = _styled19("div").withConfig({
  componentId: "sc-sf-ij24gx-18"
})`${p => p.$_css12}`;
var _StyledDiv18 = _styled20("div").withConfig({
  componentId: "sc-sf-ij24gx-19"
})({
  "display": "flex",
  "flexDirection": "column",
  "alignItems": "flex-start",
  "gap": "0.8rem",
  "padding": "2.4rem"
});
var _StyledH = _styled21("h3").withConfig({
  componentId: "sc-sf-ij24gx-20"
})`${p => p.$_css13}`;
var _StyledLink = _styled22(Link).withConfig({
  componentId: "sc-sf-ij24gx-21"
})`${p => p.$_css14}`;
var _StyledSpan = _styled23("span").withConfig({
  componentId: "sc-sf-ij24gx-22"
})({
  "display": "block"
});
var _StyledDiv19 = _styled24("div").withConfig({
  componentId: "sc-sf-ij24gx-23"
})({
  "position": "relative",
  "marginTop": "0.2rem",
  "height": "0.1rem"
});
var _StyledDiv20 = /*#__PURE__*/_styled25("div").withConfig({
  displayName: "nav-dropdown___StyledDiv20",
  componentId: "sc-131ypfb-0"
})(["", ""], p => p.$_css15);